import { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePrimaryColor,
  usePropertyName,
  usePropertyField,
  usePropertyAddressUrl,
  useSocialLinks,
  useTokens,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import {
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  YouTubeIcon,
  LinkedInIcon,
  TikTokIcon,
  multihubLogoMonoBlackUrl,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
// Local black variant of the Repli wordmark — `@vibesites/assets` only ships
// `repliWhiteLogoUrl`, so we keep a dark-fill copy alongside it for use on
// light primary backgrounds. Source SVG mirrors `repli_white.svg` with the
// wordmark's `fill:#fff` swapped to `#1a1a1a` (matches the dark color used
// by the tokens generator's `getContrastTextColor`).
import repliBlackLogoUrl from '../assets/repli_black.svg';
// Haven Realty Capital footer badge (white wordmark on charcoal) — the same
// mark as https://havenrc.com/static/assets/images/footer-logo.png. Bundled
// locally so the Managed By credit doesn't depend on the production CDN, and
// so we never run it through the bottom-bar whitening filter (that filter is
// for black line-art marks; this badge already carries its own background).
import havenFooterLogoUrl from '../assets/haven-footer-logo.png';
// `getContrastTextColor` (and the TEXT_ON_PRIMARY_* constants) live in
// ../utils/luminance — a single source of truth shared with the Header, rather
// than being re-implemented here. It mirrors the tokens generator's
// `getContrastTextColor` verbatim (0.55 luminance threshold, '#ffffff' /
// '#1a1a1a' return values), keeping logo/text contrast choices in lockstep with
// the auto-computed `--vibe-color-text-on-primary` CSS variable.
import {
  getAccessibleTextOnBackground,
  TEXT_ON_PRIMARY_LIGHT,
} from '../utils/luminance';

// ============================================================================
// Types
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/** Formats a phone string into XXX.XXX.XXXX (dot-separated, per the reference footer). Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // Strip leading country code "1" if 11 digits
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
}

/**
 * Splits a single-line address into a street line and a "city, state zip" line.
 * Drops a trailing country segment (USA / United States) so the second line
 * stops at the ZIP. Falls back to a single line when the address has no commas.
 *
 * "502 S Fremont Ave, Tampa, FL 33606, USA"
 *   → { line1: "502 S Fremont Ave", line2: "Tampa, FL 33606" }
 */
function splitAddressLines(full: string | null | undefined): { line1: string; line2: string } {
  const cleaned = (full || '')
    .trim()
    .replace(/,?\s*(usa|u\.s\.a\.|united states(?: of america)?)\.?\s*$/i, '')
    .trim();
  const parts = cleaned.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) return { line1: cleaned, line2: '' };
  return { line1: parts[0], line2: parts.slice(1).join(', ') };
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  // Already has am/pm — just normalize casing
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  // Parse 24h "HH:MM" or "H:MM"
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  label: string;
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Sort by canonical day order
  const sorted = [...raw].sort((a, b) => {
    const ai = DAY_ORDER.indexOf(normalizeDay(a.day || ''));
    const bi = DAY_ORDER.indexOf(normalizeDay(b.day || ''));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const groups: GroupedHours[] = [];

  for (const entry of sorted) {
    if (!hasValue(entry.day)) continue;

    const key = normalizeDay(entry.day);
    const dayLabel = DAY_LABELS[key] || entry.day;
    const dayShortLabel = DAY_SHORT_LABELS[key] || (entry.day || '').slice(0, 3);
    const isClosed = !hasValue(entry.open_time) && !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    // Only collapse consecutive days into a range when they share *open* hours.
    // Closed days always get their own line (e.g. "Saturday / Closed" and
    // "Sunday / Closed" as two separate rows) rather than being merged into a
    // single "Saturday - Sunday / Closed" range.
    if (prev && prev.hours === hoursStr && !isClosed) {
      // Extend the previous group's range — keep both the full and short label
      // ranges in sync so the responsive renderer can swap them at the tablet
      // breakpoint without re-deriving the grouping.
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayLabel, shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

/**
 * Expands a (possibly partial) set of office-hour entries into the full Mon–Sun
 * week, so days the property omitted from the collection (typically the
 * weekend) still render as their own "Closed" line rather than silently
 * disappearing. Existing entries win; any missing day becomes a closed
 * placeholder. Order follows `DAY_ORDER` so `groupOfficeHours` can range-merge
 * consecutive open days. Callers should only invoke this when the property has
 * at least one configured entry — an empty collection stays empty so the
 * "hours will be displayed here" fallback still shows.
 */
function fillWeek(raw: OfficeHour[]): OfficeHour[] {
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (!byDay.has(key)) byDay.set(key, entry);
  }
  return DAY_ORDER.map(
    (key) => byDay.get(key) ?? { day: DAY_LABELS[key], open_time: null, close_time: null }
  );
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    // Defaults to the primary property logo — the same "property logo" the
    // header uses (its full wordmark), rather than the mono-white icon mark,
    // which for some properties is just a brand glyph (e.g. a paw). The
    // runtime swaps to the mono-white variant on dark primary backgrounds so
    // the mark stays legible; see runtimeLogoSlotDef below.
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_white_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    url: { label: 'URL', type: 'url', defaultField: 'property_apply_now_url', defaultValue: '#' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// "Managed By" credit — Haven Realty Capital's footer badge. This company theme
// always manages under Haven RC, so we pin the default to the production
// footer mark (CDN URL as the static schema default; the local PNG is what
// actually renders — see havenFooterLogoUrl). Property company-logo fields are
// still offered as overrides for one-off swaps. alt / url still bind to the
// company name and company URL.
const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: {
      label: 'Company Logo',
      type: 'image',
      defaultValue: 'https://havenrc.com/static/assets/images/footer-logo.png',
      suggestedFields: ['property_company_logo', 'property_company_mono_black_logo', 'property_company_mono_white_logo'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'property_company_name', defaultValue: 'Haven Realty Capital' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url', defaultValue: 'https://havenrc.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

/** CDN URL used as the schema default for Managed By — swapped for the bundled PNG at render. */
const HAVEN_FOOTER_LOGO_CDN = 'https://havenrc.com/static/assets/images/footer-logo.png';

// "Powered By" credit — the Multihub wordmark. Mirrors designByLogoDef: the mark
// itself is a fixed asset (multihubLogoMonoBlackUrl, the black variant for the
// light bottom bar), so the slot only carries the alt text, link URL, and target.
const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Main Street, City, ST 00000' },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place/business/address
    // fields). An explicit override from the editor still takes precedence.
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility Statement' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};


// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phone = useContentToken('footer.phone');

  // ---- Contrast-driven logo variant selection ----
  // Pick mono_white / mono_black logo defaults (and the Multihub + Repli marks)
  // to match the WCAG-accessible on-primary text color (same logic Header uses
  // to override `--vibe-color-text-on-primary` site-wide).
  const primaryColor = usePrimaryColor();
  const themeColors = useTokens().color;
  const textOnPrimary = getAccessibleTextOnBackground(primaryColor, themeColors.textOnPrimary);
  const useWhiteOnPrimary = textOnPrimary === TEXT_ON_PRIMARY_LIGHT;

  // ---- Bottom-bar contrast decision ----
  // The bottom strip sits on the primary color like the main footer, so it
  // shares the on-primary text color. The black-variant credit marks (Repli /
  // Multihub) are whitened with `brightness(0) invert(1)` on dark primaries.
  const bottomBarFg = textOnPrimary;
  const whitenBottomBarLogo = useWhiteOnPrimary;

  // The footer logo mirrors the HEADER's logo so the two always show the same
  // mark. The header renders the property's mono-black wordmark, falling back to
  // the full-color primary logo when no mono-black exists (see Header.tsx). We
  // resolve the same source here.
  //
  // We deliberately DON'T default to `property_mono_white_logo` on dark
  // backgrounds: for some properties that field is only a brand glyph rather
  // than the full wordmark (AMC's Fairfield Lakes ships a bare paw print there),
  // so keying off it swapped in the wrong mark. Instead we take the header's
  // mark and recolor it to white with a CSS `brightness(0) invert(1)` filter
  // when the background is dark (see `whitenLogo` in the JSX below). That filter
  // flattens ANY source logo — dark, colored, or already white — to a clean
  // white silhouette, so it works regardless of which logo variants a property
  // has configured, including the black-on-black case (Weidner 4800 Excelsior)
  // that started this.
  const monoBlackLogo = usePropertyField('property_mono_black_logo');
  const logoDefaultField = hasValue(monoBlackLogo)
    ? 'property_mono_black_logo'
    : 'property_primary_logo';

  const runtimeLogoSlotDef: SlotGroupDefinition = {
    ...logoSlotDef,
    fields: {
      ...logoSlotDef.fields,
      image: { ...logoSlotDef.fields.image, defaultField: logoDefaultField },
    },
  };

  // On a dark primary background, recolor whatever logo is showing to white so
  // it stays legible (a dark or full-color mark would otherwise wash out). On a
  // light background the mono-black mark already reads correctly, so no filter.
  const whitenLogo = useWhiteOnPrimary;

  const propertyName = usePropertyName();

  const logo = useSlotGroup('footer.logo', runtimeLogoSlotDef);
  const applyNowLink = useSlotGroup('footer.apply_now_link', applyNowLinkDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  // Prefer the bundled Haven footer badge whenever the slot still points at the
  // schema default (or is empty). Custom editor uploads / property-field picks
  // pass through unchanged.
  const managedByImageUrl =
    !hasValue(managedByLogo.image) || managedByLogo.image === HAVEN_FOOTER_LOGO_CDN
      ? havenFooterLogoUrl
      : managedByLogo.image;
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — the
  // hook builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  // Fill out the full week so any weekend/off days the property left out of the
  // collection still show as their own "Closed" line (matching the reference
  // footer). Only when there's at least one configured entry — otherwise stay
  // empty so the placeholder fallback renders instead of seven "Closed" rows.
  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(fillWeek(officeHours))
    : [];

  // ---- Social links ----
  // Sourced directly from property fields via `useSocialLinks()`. Only the
  // platforms with a configured URL render, each as a circular icon button on
  // the primary background.
  const social = useSocialLinks();
  const socialLinks = [
    { url: social.facebook, Icon: FacebookIcon, label: 'Facebook' },
    { url: social.instagram, Icon: InstagramIcon, label: 'Instagram' },
    { url: social.twitter, Icon: TwitterIcon, label: 'Twitter' },
    { url: social.youtube, Icon: YouTubeIcon, label: 'YouTube' },
    { url: social.linkedin, Icon: LinkedInIcon, label: 'LinkedIn' },
    { url: social.tiktok, Icon: TikTokIcon, label: 'TikTok' },
  ].filter((s): s is { url: string; Icon: typeof FacebookIcon; label: string } => hasValue(s.url));

  // ---- Two-line address ----
  // Prefer structured property fields (street on line 1, "City, ST ZIP" on
  // line 2, no country). Fall back to parsing the single-line address text —
  // which also honours any editor override on the address slot.
  const addrStreet1 = usePropertyField('property_address_1');
  const addrStreet2 = usePropertyField('property_address_2');
  const addrCity = usePropertyField('property_address_city');
  const addrState = usePropertyField('property_address_state');
  const addrZip = usePropertyField('property_address_zip');

  const structuredStreet = [addrStreet1, addrStreet2].filter(hasValue).join(', ');
  const structuredCityStateZip = [
    hasValue(addrCity) ? addrCity : '',
    [addrState, addrZip].filter(hasValue).join(' '),
  ]
    .filter((p) => p && p.trim())
    .join(', ');

  const addressText = addressLink.text || '123 Main Street, City, ST 00000';
  const parsedAddress = splitAddressLines(addressText);
  const addressLine1 = hasValue(structuredStreet) ? structuredStreet : parsedAddress.line1;
  const addressLine2 = hasValue(structuredCityStateZip)
    ? structuredCityStateZip
    : parsedAddress.line2;

  // ---- Copyright ----
  // `property_copyright` is a free-text field, so it may be blank or omit the
  // year / © symbol. When the editor hasn't set (or the property doesn't carry)
  // an explicit value, compute a sensible "© {year} {company}" fallback. Company
  // name is preferred for the credit; property name is the secondary fallback.
  // getFullYear() runs at render — the workflow-script date restriction doesn't
  // apply to component render; SSG stamps the build year, which is fine here.
  const copyright = useContentToken('footer.copyright');
  const companyName = usePropertyField('property_company_name');
  const copyrightOwner = hasValue(companyName) ? companyName : (propertyName || '');
  const copyrightFallback = `© ${new Date().getFullYear()}${copyrightOwner ? ` ${copyrightOwner}` : ''}`;
  const copyrightText = hasValue(copyright) ? copyright : copyrightFallback;

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  // The Yardi RentCafe IQ widget is injected at runtime by an external script.
  // It (a) ships under several DOM signatures across versions and (b) sets its
  // own inline `style="z-index: …"` on its outer container after mount, which
  // beats anything a stylesheet declares. The CSS rule further down in the
  // render covers the common selectors as a cheap first pass; this effect is
  // the authoritative fix.
  //
  // Approach: watch document.body for any added/changed node. For each element
  // whose id/class/src/title/name matches a RentCafe-IQ-ish pattern, find the
  // outermost `position: fixed` ancestor (the actual floating container, which
  // is what controls stacking against the rest of the page) and pin its
  // z-index to the max signed 32-bit int with `!important`. `setProperty` with
  // the `important` priority is the only way to override an inline style set
  // by the widget's own JS.
  //
  // Scoped to the Footer because Footer is rendered on every page via
  // DefaultLayout, so we get a single global mount point without having to
  // introduce a new always-on component.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // Patterns covering the DOM signatures Yardi ships the RentCafe IQ /
    // "RentCafe Connect" webchat widget under. The current production build
    // uses the `rcc-app-*` class prefix and the `conversations-webchat-toggle`
    // / `webchat-toggle-button` launcher; older / alternate builds have
    // surfaced under rentcafe / rciq / CRM IQ / Yardi / Yvenue branding, so
    // we keep those entries too as a defense against future rebrands.
    const PATTERNS = [
      /\brcc-app/i,
      /\bwebchat-toggle/i,
      /conversations-webchat/i,
      /rentcafe/i,
      /rciq/i,
      /\bcrm[-_ ]?iq\b/i,
      /\biq[-_ ]?(chat|bot)/i,
      /yardi/i,
      /yvenue/i,
    ];

    const Z = '2147483647';

    function attrHaystack(el: Element): string {
      const id = el.id || '';
      const cls = typeof el.className === 'string'
        ? el.className
        : ((el.className as unknown as { baseVal?: string })?.baseVal || '');
      const src = el.getAttribute('src') || '';
      const title = el.getAttribute('title') || '';
      const name = el.getAttribute('name') || '';
      // Include data-* attributes — Yardi widgets sometimes only brand via those.
      let data = '';
      for (const a of Array.from(el.attributes)) {
        if (a.name.startsWith('data-')) data += ` ${a.name} ${a.value}`;
      }
      return `${id} ${cls} ${src} ${title} ${name}${data}`;
    }

    function looksLikeRciq(el: Element): boolean {
      const hay = attrHaystack(el);
      return PATTERNS.some(p => p.test(hay));
    }

    function outermostFixedAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el;
      let last: HTMLElement | null = null;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        const pos = getComputedStyle(cur).position;
        if (pos === 'fixed') last = cur;
        cur = cur.parentElement;
      }
      return last;
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!looksLikeRciq(el)) return;
      // Pin the matched element itself (in case it's already the fixed container)
      // and also the outermost fixed ancestor (covers wrappers / portals).
      pin(el);
      const fixedAncestor = outermostFixedAncestor(el);
      if (fixedAncestor && fixedAncestor !== el) pin(fixedAncestor);
    }

    function scanSubtree(root: ParentNode) {
      // Scope the query so we don't enumerate the whole document on every tick.
      const candidates = root.querySelectorAll('iframe, div, aside, section, button');
      for (const c of Array.from(candidates)) processCandidate(c);
      // The root node itself can also be a match (querySelectorAll skips it).
      if (root instanceof Element) processCandidate(root);
    }

    // Initial pass — catches the widget if it's already mounted by the time
    // the Footer hydrates (e.g. injected from <head> before React boots).
    scanSubtree(document.body);

    // Coalesce bursts of mutations into a single rAF-scheduled scan so we
    // don't thrash on chat widgets that animate (every transform tick fires
    // an attribute mutation).
    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Only react to attribute changes that could affect identification or
      // stacking — anything else (e.g. inline transforms during animation) is
      // noise we want to ignore.
      attributeFilter: ['id', 'class', 'src', 'title', 'name', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <footer className="w-full">

      {/* ==================================================================== */}
      {/* Main Footer — sits on the property's primary brand color.            */}
      {/* Layout mirrors the reference: brand + quick links on the left, then  */}
      {/* phone, address, and office hours spread across the middle, with the  */}
      {/* social icons anchored to the right edge on desktop.                  */}
      {/* ==================================================================== */}
      <div
        className="text-primary-foreground"
        style={{ backgroundColor: 'var(--vibe-color-primary)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-10 lg:py-16">
          <div className="flex flex-col gap-6 md:gap-6 lg:flex-row lg:items-start lg:justify-between lg:gap-8">

            {/* Brand: logo + Apply Now quick link (no resident portal yet) */}
            {/* Centered on tablet (matches reference), left-aligned on desktop. */}
            <div className="flex flex-col items-center gap-6 text-center md:gap-4 md:items-center md:text-center lg:items-start lg:gap-6 lg:text-left">
              <EditableSlotGroup groupPath="footer.logo" definition={runtimeLogoSlotDef}>
                <a href="/" className="block">
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logo.alt || 'Community Logo'}
                      className="h-16 md:h-20 w-auto max-w-[240px] object-contain"
                      sizes="240px"
                      style={whitenLogo ? { filter: 'brightness(0) invert(1)' } : undefined}
                    />
                  ) : (
                    <div className="font-heading text-3xl md:text-4xl uppercase tracking-wide text-primary-foreground">
                      {propertyName || 'Our Community'}
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              <div className="flex flex-col items-center gap-2 font-body text-base font-semibold uppercase tracking-[0.08em] md:flex-row md:gap-6 md:justify-center lg:justify-start">
                <EditableSlotGroup groupPath="footer.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '#'}
                    target={applyNowLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="text-primary-foreground hover:text-[var(--vibe-color-text-on-primary)]/70 transition-colors"
                  >
                    {applyNowLink.text || 'Apply Now'}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>

            {/* Details: phone / address / office hours / social. Stacks on
                mobile, spreads as a horizontal row on tablet (matches the
                reference), and flattens back into the outer flex row on desktop
                via `lg:contents` so all five columns space evenly. */}
            <div className="flex flex-col items-center gap-3 md:flex-row md:items-start md:justify-between md:gap-6 lg:contents">

            {/* Phone */}
            {/* Nudged up 1px on tablet and ~2px on desktop to line the phone's
                cap-top up with the text-sm "Monday" label beside it. The desktop
                offset is larger because the phone jumps to text-lg there (taller
                line-box); on tablet it's text-sm like the hours, so 1px suffices. */}
            <div className="flex flex-col justify-center md:-mt-px lg:-mt-0.5">
              <a
                href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref whitespace-nowrap font-body text-sm font-normal md:text-sm lg:text-lg text-primary-foreground hover:text-[var(--vibe-color-text-on-primary)]/70 transition-colors"
              >
                <EditableToken
                  path="footer.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>

            {/* Address */}
            {/* Same hair-of-a-nudge as the phone: text-base line-box sits a touch
                lower than the text-sm hours label when their tops are aligned. */}
            <div className="flex flex-col justify-center -mt-0.5 md:-mt-0.5">
              <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="font-body text-sm md:text-sm lg:text-base leading-relaxed text-center md:text-left text-[var(--vibe-color-text-on-primary)]/80 hover:text-primary-foreground transition-colors"
                >
                  <span className="block">{addressLine1}</span>
                  {hasValue(addressLine2) && <span className="block">{addressLine2}</span>}
                </a>
              </EditableSlotGroup>
            </div>

            {/* Office Hours */}
            <div className="flex w-full flex-col justify-center md:w-auto">
              {groupedHours.length > 0 ? (
                <dl className="grid w-full grid-cols-[auto_auto] justify-between gap-x-8 gap-y-1.5 px-7 font-body text-sm md:w-auto md:grid-cols-[auto_1fr] md:justify-start md:px-0">
                  {groupedHours.map((group, index) => {
                    // Ranges (e.g. "Monday - Friday") always use the 3-letter
                    // abbreviated form ("Mon - Fri") so they stay compact; single
                    // days keep their full name. The tighter tablet range keeps
                    // everything abbreviated regardless.
                    const isRange = group.label.includes(' - ');
                    const primaryLabel = isRange ? group.shortLabel : group.label;
                    return (
                    <div key={index} className="contents">
                      <dt className="whitespace-nowrap text-primary-foreground">
                        {/* Full names for single days on wide layouts, abbreviated
                            for ranges; always abbreviated on the tighter tablet
                            range where the row would otherwise wrap. */}
                        <span className="md:hidden lg:inline">{primaryLabel}</span>
                        <span className="hidden md:inline lg:hidden">{group.shortLabel}</span>
                      </dt>
                      <dd className="whitespace-nowrap text-right text-[var(--vibe-color-text-on-primary)]/80">{group.hours}</dd>
                    </div>
                    );
                  })}
                </dl>
              ) : (
                <p className="font-body text-sm text-[var(--vibe-color-text-on-primary)]/85">Office hours will be displayed here.</p>
              )}
            </div>

            {/* Social icons — only render platforms that have a configured URL */}
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-3 mt-3 md:mt-0">
                {socialLinks.map(({ url, Icon, label }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-[var(--vibe-color-text-on-primary)] text-primary-foreground hover:bg-[var(--vibe-color-text-on-primary)]/10 transition-colors"
                  >
                    <Icon size={16} color="currentColor" aria-label={label} />
                  </a>
                ))}
              </div>
            )}

            </div>

          </div>
        </div>
      </div>

      {/* ==================================================================== */}
      {/* Bottom Bar — primary-color strip: compliance marks on the left,      */}
      {/* legal links centered, and the "Website Design by" credit on the      */}
      {/* right. A hairline top border separates it from the main footer.      */}
      {/* ==================================================================== */}
      <div
        className="border-t border-[color-mix(in_srgb,currentColor_15%,transparent)]"
        style={{ backgroundColor: 'var(--vibe-color-primary)', color: bottomBarFg }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col items-center gap-6 md:flex-row md:flex-nowrap md:justify-between md:gap-3 lg:gap-6">

            {/* Compliance icons */}
            <div className="flex items-center gap-5 py-2 md:py-0 md:gap-3 lg:gap-5 [&_svg]:h-6 [&_svg]:w-6 md:[&_svg]:h-[18px] md:[&_svg]:w-[18px] lg:[&_svg]:h-7 lg:[&_svg]:w-7" style={{ color: bottomBarFg }}>
              <FairHousingIcon size={28} color="currentColor" />
              {/* Paw + wheelchair are FontAwesome icons sized via inline
                  `style` (width/height px), which beats plain CSS classes — so
                  the parent `[&_svg]` sizing can't touch them. Use `!important`
                  utilities to override the inline size on tablet, and pull them
                  a bit under the HUD icon's 18px box since their solid glyphs
                  fill the whole viewBox and otherwise read larger. */}
              <PetsAllowedIcon size={28} color="currentColor" className="!h-6 !w-6 md:!h-[15px] md:!w-[15px]" />
              <WheelchairIcon size={28} color="currentColor" className="!h-6 !w-6 md:!h-[15px] md:!w-[15px]" />
            </div>

            {/* Legal nav links with pipe separators */}
            <nav className="flex flex-col items-center gap-y-2 font-body text-xs font-semibold uppercase tracking-[0.08em] md:flex-row md:flex-nowrap md:items-center md:justify-center md:whitespace-nowrap md:gap-x-2 md:gap-y-1 md:text-xs md:tracking-[0.03em] lg:gap-x-3 lg:text-xs lg:tracking-[0.08em]" aria-label="Legal links">
              <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
                <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className="hover:opacity-70 transition-opacity">
                  {privacyPolicyLink.text || 'Privacy Policy'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true" className="hidden opacity-40 md:inline">|</span>
              <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
                <a href={accessibilityLink.url || '/accessibility'} target={accessibilityLink._target || '_self'} className="hover:opacity-70 transition-opacity">
                  {accessibilityLink.text || 'Accessibility Statement'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true" className="hidden opacity-40 md:inline">|</span>
              <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className="hover:opacity-70 transition-opacity">
                  {sitemapLink.text || 'Sitemap'}
                </a>
              </EditableSlotGroup>
            </nav>

            {/* Website Design By (Repli) — black wordmark on the light surface */}
            <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
              <a
                href={designByLogo.url || 'https://www.repli360.com/'}
                target={designByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 md:flex-row md:gap-1.5 lg:gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.design_by_text"
                  defaultValue="<p>Website Design by</p>"
                  as="span"
                  className="whitespace-nowrap font-body text-xs font-semibold uppercase tracking-[0.08em] md:text-xs md:tracking-[0.03em] lg:text-xs lg:tracking-[0.08em]"
                />
                <VibesitesImage
                  url={repliBlackLogoUrl}
                  alt={designByLogo.alt || 'Repli Logo'}
                  height={18}
                  className="h-[36px] w-auto md:h-[19px] lg:h-[18px]"
                  sizes="120px"
                  priority
                  style={whitenBottomBarLogo ? { filter: 'brightness(0) invert(1)' } : undefined}
                />
              </a>
            </EditableSlotGroup>

          </div>

          {/* Second row: management / platform credits + copyright.
              A separate row under the existing compliance + legal + "Website
              Design by" row (the Blue Haven layout does not mirror the Verso
              reference arrangement — it just carries the same new elements).
              Managed By and Powered By flank the copyright, which centers on
              desktop. All black-variant marks, matching the light bottom bar. */}
          <div className="mt-5 flex flex-col items-center gap-4 border-t border-[color-mix(in_srgb,currentColor_12%,transparent)] pt-5 md:flex-row md:flex-nowrap md:items-center md:justify-between md:gap-3 lg:gap-6">

            {/* Managed By — Haven Realty Capital footer badge, linking to the
                company site. Always rendered for this company theme (the badge
                is bundled); do NOT whiten it — the mark is a gray plate with
                white type and would wash out under brightness/invert. */}
            <EditableSlotGroup groupPath="footer.managed_by_logo" definition={managedByLogoDef}>
              <a
                href={managedByLogo.url || 'https://havenrc.com/'}
                target={managedByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 md:flex-row md:gap-1.5 lg:gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.managed_by_text"
                  defaultValue="<p>Managed By</p>"
                  as="span"
                  className="whitespace-nowrap font-body text-xs font-semibold uppercase tracking-[0.08em] md:tracking-[0.03em] lg:tracking-[0.08em]"
                />
                <VibesitesImage
                  url={managedByImageUrl}
                  alt={managedByLogo.alt || 'Haven Realty Capital'}
                  height={36}
                  className="h-[48px] w-auto max-w-[180px] object-contain md:h-[36px]"
                  sizes="180px"
                  priority
                />
              </a>
            </EditableSlotGroup>

            {/* Copyright — property_copyright, else computed "© {year} {owner}".
                On mobile (stacked column) it drops to the bottom of the three
                items via order-last; on md+ the row resets to DOM order so it
                sits centered between Managed By and Powered By. */}
            <div className="order-last md:order-none font-body text-xs text-center opacity-70">
              <EditableToken
                path="footer.copyright"
                defaultField="property_copyright"
              >
                {copyrightText}
              </EditableToken>
            </div>

            {/* Powered By — Multihub wordmark (black variant for the light bar) */}
            <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
              <a
                href={poweredByLogo.url || 'https://www.multihub.io/'}
                target={poweredByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 md:flex-row md:gap-1.5 lg:gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.powered_by_text"
                  defaultValue="<p>Powered By</p>"
                  as="span"
                  className="whitespace-nowrap font-body text-xs font-semibold uppercase tracking-[0.08em] md:tracking-[0.03em] lg:tracking-[0.08em]"
                />
                <VibesitesImage
                  url={multihubLogoMonoBlackUrl}
                  alt={poweredByLogo.alt || 'Multihub Logo'}
                  height={16}
                  className="h-[16px] w-auto md:h-[16px]"
                  sizes="140px"
                  priority
                  style={whitenBottomBarLogo ? { filter: 'brightness(0) invert(1)' } : undefined}
                />
              </a>
            </EditableSlotGroup>

          </div>
        </div>
      </div>

      {/* ---- Global z-index fallback for the RentCafe IQ chatbot ----
          This is a cheap first pass to cover the common DOM signatures Yardi
          has shipped (iframe, container div, launcher button). The
          authoritative fix lives in the `useEffect` above this `return` — it
          uses a MutationObserver and inline `!important` to handle widget
          variants whose selectors we don't know and to beat the inline
          z-index the widget sets via its own JS after mount. */}
      <style>{`
        /* ── Body-copy line-height knobs ───────────────────────────────────
           THE single place to tune how tightly body text runs, site-wide.
           Footer is rendered as global chrome on every page, so these rules
           apply throughout. Lower = tighter, higher = looser.

           Targets the theme's body-prose signatures (font-body + leading-*).
           The two-class selectors outrank Tailwind's single-class .leading-*
           utilities on specificity, so they win WITHOUT !important. Headings
           (font-heading) and small labels (leading-snug) are left untouched.

           "base" applies at all widths; the "mobile" (≤767px) and "tablet"
           (768–1024px) blocks sit AFTER it with equal specificity, so each
           wins inside its own (non-overlapping) range only. */
        :root {
          --bh-body-leading: 1.55;             /* base body copy; Tailwind leading-relaxed is 1.625 */
          --bh-body-leading-loose: 1.85;       /* stylized CTA panels; leading-loose is 2 */
          --bh-mobile-body-leading: 1.45;      /* mobile: tighter than base for narrow measures */
          --bh-mobile-body-leading-loose: 1.7;
          --bh-tablet-body-leading: 1.5;       /* tablet: a touch tighter than base */
          --bh-tablet-body-leading-loose: 1.75;
          --bh-mobile-list-leading: 1.1;       /* mobile: tighter still for bullet-list items */
        }
        .font-body.leading-relaxed { line-height: var(--bh-body-leading); }
        .font-body.leading-loose   { line-height: var(--bh-body-leading-loose); }
        /* Inter-item spacing for native prose bullet lists, site-wide (all
           breakpoints). A touch tighter than the assorted per-component
           [&_li]:mb-* defaults so bulleted items stack a little closer. The
           :not(.af-list) guard skips the amenities feature lists, which space
           their items with a flex "gap" (tuned in AmenitiesFeatures) and would
           otherwise get this margin stacked ON TOP of that gap. The two-class +
           element selector outranks Tailwind's single-class mb-* utilities. */
        .font-body:not(.af-list) ul li { margin-bottom: 0.2rem; }
        @media (max-width: 767px) {
          .font-body.leading-relaxed { line-height: var(--bh-mobile-body-leading); }
          .font-body.leading-loose   { line-height: var(--bh-mobile-body-leading-loose); }
          /* Bullet-list items run tighter than surrounding prose on the narrow
             mobile measure. A direct rule on the <li> overrides the line-height
             it would otherwise inherit from the .font-body prose container, so
             this wins regardless of the container's leading-* utility. */
          .font-body ul li { line-height: var(--bh-mobile-list-leading); }
          /* The tight list line-height above squeezes the native disc glyph's
             line box, which makes markers look vertically squished. Shrinking
             the marker font-size gives the round disc room to render as a true
             circle and trims it down a touch for the smaller mobile measure.
             (.af-list uses list-style:none, so it has no ::marker and is
             unaffected — its dots are styled separately in AmenitiesFeatures.) */
          .font-body ul li::marker { font-size: 0.8em; }
        }
        @media (min-width: 768px) and (max-width: 1024px) {
          .font-body.leading-relaxed { line-height: var(--bh-tablet-body-leading); }
          .font-body.leading-loose   { line-height: var(--bh-tablet-body-leading-loose); }
        }

        /* Production RentCafe Connect / IQ build (confirmed against live DOM):
           the floating wrap is .rcc-app-cta-wrap, the launcher is
           #conversations-webchat-toggle.webchat-toggle-button. We target the
           wrap (and the inner controls layer) so the entire stacking context
           gets lifted, not just the bubble. */
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        [class*="rcc-app-"],
        [id*="conversations-webchat"],
        [class*="webchat-toggle"],
        /* Legacy / alternate Yardi signatures, kept as a hedge against
           rebrands and per-property build variants. */
        iframe[src*="rentcafeiq"],
        iframe[src*="rentcafe-iq"],
        iframe[src*="rentcafe.com/iq"],
        iframe[src*="yardi"],
        iframe[src*="yvenue"],
        iframe[title*="RentCafe IQ" i],
        iframe[title*="Rent Cafe IQ" i],
        iframe[title*="CRM IQ" i],
        iframe[title*="webchat" i],
        [id^="rciq"],
        [id*="rentcafeiq"],
        [id*="rentcafe-iq"],
        [id*="rentcafe_iq"],
        [id*="iq-chat"],
        [id*="iqchat"],
        [id*="iq-bot"],
        [id*="iqbot"],
        [class^="rciq-"],
        [class*=" rciq-"],
        [class*="rentcafeiq"],
        [class*="rentcafe-iq"],
        [class*="rentcafe_iq"],
        [class*="iq-chat"],
        [class*="iqchat"],
        [class*="yardi-chat"],
        [class*="yvenue"],
        [data-rciq],
        [data-rentcafeiq] {
          z-index: 2147483647 !important;
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Managed By</p>',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered By</p>',
    },
    'footer.copyright': {
      label: 'Copyright',
      type: 'text',
      // No defaultValue: when `property_copyright` is unset the token resolves to
      // empty, so the component's computed "© {current year} {owner}" fallback
      // (which always reflects the current year) takes over instead of a stale
      // hardcoded year.
      defaultField: 'property_copyright',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.apply_now_link': applyNowLinkDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.address': addressLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
